import type { AppQueryConfig } from "QueryTypes";
import { clubhouseApiHeadersV1 } from "utils/api/apiHelper";
import type { SimulatorReservation, SimulatorReservationPayload } from "Models";
import { config } from "@/config/env";

const urls = {
  simulatorReservationsBase(): string {
    return `${config.apiBaseUrl}/api/simulator-reservations/`;
  },
  simulatorReservationById(simulatorReservationId: string): string {
    return `${config.apiBaseUrl}/api/simulator-reservations/${simulatorReservationId}`;
  },
  getSimulatorReservations(
    facilityId: string,
    startTime?: string,
    endTime?: string,
  ): string {
    return startTime && endTime
      ? `${config.apiBaseUrl}/api/facilities/${facilityId}/simulator-reservations?startTime=${startTime}&endTime=${endTime}`
      : `${config.apiBaseUrl}/api/facilities/${facilityId}/simulator-reservations`;
  },
};

export const makeCreateSimulatorReservationMutation = (
  facilityId: string,
  body: SimulatorReservationPayload,
): AppQueryConfig => ({
  url: urls.simulatorReservationsBase(),
  queryKey: `createSimulatorReservation:${facilityId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "POST",
  },
  body,
  transform: (responseJson: SimulatorReservation) => {
    return {
      simulatorReservations: {
        byFacilityId: {
          [facilityId]: { [responseJson.id]: responseJson },
        },
      },
    };
  },
  update: {
    simulatorReservations: (oldValue, newValue) => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...{
          [facilityId]: {
            ...oldValue?.byFacilityId?.[facilityId],
            ...newValue.byFacilityId[facilityId],
          },
        },
      },
    }),
  },
});

export const getSimulatorReservationsByFacilityId = (
  facilityId: string,
  startTime?: string,
  endTime?: string,
): AppQueryConfig => ({
  url: urls.getSimulatorReservations(facilityId, startTime, endTime),
  queryKey:
    startTime && endTime
      ? `getSimulatorReservations:${facilityId}-${startTime}-${endTime}`
      : `getSimulatorReservations:${facilityId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "GET",
  },
  transform: (responseJson: SimulatorReservation[]) => {
    const obj: { [id: string]: SimulatorReservation } = {};
    responseJson.map((sim) => (obj[sim.id] = sim));
    return {
      simulatorReservations: {
        byFacilityId: {
          [facilityId]: obj,
        },
      },
    };
  },
  update: {
    simulatorReservations: (oldValue, newValue) => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...{
          [facilityId]: {
            ...oldValue?.byFacilityId?.[facilityId],
            ...newValue.byFacilityId[facilityId],
          },
        },
      },
    }),
  },
});

export const makeDeleteSimulatorReservationMutation = (
  simulatorReservationId: string,
  facilityId: string,
): AppQueryConfig => ({
  url: urls.simulatorReservationById(simulatorReservationId),
  queryKey: `deleteSimulatorReservation:${simulatorReservationId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "DELETE",
  },
  optimisticUpdate: {
    simulatorReservations: (reduxState) => {
      delete reduxState.byFacilityId[facilityId][simulatorReservationId];
      return reduxState;
    },
  },
});

export const makeUpdateSimulatorReservationMutation = (
  simulatorReservationId: string,
  facilityId: string,
  body: SimulatorReservationPayload,
): AppQueryConfig => ({
  url: urls.simulatorReservationById(simulatorReservationId),
  queryKey: `makeUpdateSimulatorReservationMutation:${simulatorReservationId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "PATCH",
  },
  body,
  transform: (responseJson: SimulatorReservation) => {
    return {
      simulatorReservations: {
        byFacilityId: {
          [facilityId]: { [responseJson.id]: responseJson },
        },
      },
    };
  },
  update: {
    simulatorReservations: (oldValue, newValue) => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...{
          [facilityId]: {
            ...oldValue?.byFacilityId?.[facilityId],
            ...newValue.byFacilityId[facilityId],
          },
        },
      },
    }),
  },
});

export const makeSendSimulatorReservationToSimMutation = (
  simulatorReservationId: string,
): AppQueryConfig => ({
  url: urls.simulatorReservationById(simulatorReservationId),
  options: {
    ...clubhouseApiHeadersV1(),
    method: "POST",
  },
});
